import React, { createContext, useContext, useReducer, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  loading: true,
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'USER_LOADED':
      return {
        ...state,
        isAuthenticated: true,
        loading: false,
        user: action.payload,
      };
    case 'REGISTER_SUCCESS':
    case 'LOGIN_SUCCESS':
      localStorage.setItem('token', action.payload.token);
      return {
        ...state,
        ...action.payload,
        isAuthenticated: true,
        loading: false,
      };
    case 'REGISTER_FAIL':
    case 'AUTH_ERROR':
    case 'LOGIN_FAIL':
    case 'LOGOUT':
      localStorage.removeItem('token');
      return {
        ...state,
        token: null,
        isAuthenticated: false,
        loading: false,
        user: null,
      };
    case 'CLEAR_ERRORS':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Configurer axios avec le token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  // Charger l'utilisateur
  const loadUser = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      dispatch({ type: 'AUTH_ERROR' });
      return;
    }

    try {
      const res = await axios.get('/auth/profile');
      dispatch({
        type: 'USER_LOADED',
        payload: res.data,
      });
    } catch (err) {
      dispatch({ type: 'AUTH_ERROR' });
    }
  };

  // Inscription
  const register = async (formData) => {
    try {
      const res = await axios.post('/auth/register', formData);
      const token = res.data.token;
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      dispatch({
        type: 'REGISTER_SUCCESS',
        payload: res.data,
      });
      await loadUser();
      return { success: true, message: 'Inscription réussie!' };
    } catch (err) {
      dispatch({
        type: 'REGISTER_FAIL',
        payload: err.response?.data?.message || 'Erreur d\'inscription',
      });
      return { success: false, message: err.response?.data?.message || 'Erreur d\'inscription' };
    }
  };

  // Connexion
  const login = async (telephone, motDePasse) => {
    try {
      const res = await axios.post('/auth/login', { telephone, motDePasse });
      const token = res.data.token;
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: res.data,
      });
      
      // Ne pas appeler loadUser() car les données sont déjà dans res.data
      // Mettre directement à jour l'état avec les données reçues
      dispatch({
        type: 'USER_LOADED',
        payload: res.data,
      });
      
      return { success: true, message: 'Connexion réussie!', user: res.data };
    } catch (err) {
      dispatch({
        type: 'LOGIN_FAIL',
        payload: err.response?.data?.message || 'Erreur de connexion',
      });
      return { success: false, message: err.response?.data?.message || 'Erreur de connexion' };
    }
  };

  // Déconnexion
  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    dispatch({ type: 'LOGOUT' });
  };

  // Mettre à jour le profil
  const updateProfile = async (formData) => {
    const token = localStorage.getItem('token');
    if (!token) {
      return { success: false, message: 'Non authentifié' };
    }

    try {
      const res = await axios.put('/auth/profile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      dispatch({
        type: 'USER_LOADED',
        payload: res.data,
      });
      return { success: true, message: 'Profil mis à jour!' };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || 'Erreur de mise à jour' };
    }
  };

 useEffect(() => {
  loadUser();
}, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider
      value={{
        ...state,
        register,
        login,
        logout,
        updateProfile,
        loadUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
